import { gql } from "@apollo/client";
import { CPoint } from "../common/type";

// Base types from GraphQL schema
export type TUserRole = {
  id: number;
}

export type TWard = {
  name: string;
  displayName: string;
  districtId: number;
  district: TDistrict;
}

export type TDistrict = {
  name: string;
  displayName: string;
  provinceId: number;
  province: TProvince;
}

export type TProvince = {
  name: string;
  displayName: string;
}

export type TProject = {
  id: number;
  name: string;
  year: number;
  quater: number;
  createdAt: Date;
  updatedAt: Date;
}

export type TGeoPoint = {
  lat: number;
  lng: number;
}

export type TUser = {
  id: number;
  phoneNumber: string;
  isVerify: boolean;
  isActivated: boolean;
  location?: CPoint;
}

export type TUserLoginResponse = {
  accessToken: string;
  user: TUser;
}

export type TRole = {
  role_name: string;
}

export type TAclTypeOutput = {
  label: string;
  value: number;
}

export type TCabinetEntity = {
  id: number;
  hwVersion?: string;
  fwVersion?: string;
  appKey: string;
}

export type TCabinetInfo = {
  id: number;
  cabinetId?: number;
  identifier: string;
  PENumber?: string;
  address?: string;
  location?: CPoint;
  isRegistered: boolean;
  registeredAt?: Date;
  wardId?: number;
  ward?: TWard;
}

export type TCabinetEntityPlcData = {
  deviceId: number;
  rawData: string;
  rtcTime?: Date;
  out1Status: number;
  out2Status: number;
  br1Status: number;
  br1OnTime: string;
  br1OffTime: string;
  br2Status: number;
  br2OnTime: string;
  br2OffTime: string;
  forceState: number;
  forceTotalTime: number;
  forceCurrentTime: number;
  forceRemainTime: number;
  manualState: number;
  manualTotalTime: number;
  manualCurrentTime: number;
  manualRemainTime: number;
  doorState: number;
  disBr1: number;
  disBr2: number;
  hpsOnTime: string;
  hpsOffTime: string;
}

export type TCabinetEntityPmlcData = {
  deviceId: number;
  rawData: string;
  v1n: number;
  v2n: number;
  v3n: number;
  avgV: number;
  v12: number;
  v23: number;
  v31: number;
  avgLL: number;
  i1: number;
  i2: number;
  i3: number;
  avgI: number;
  kW1: number;
  kW2: number;
  kW3: number;
  totalkW: number;
  kVA1: number;
  kVA2: number;
  kVA3: number;
  totalkVA: number;
  kVAr1: number;
  kVAr2: number;
  kVAr3: number;
  totalkVAr: number;
  PF1: number;
  PF2: number;
  PF3: number;
  avgPF: number;
  freq: number;
  kWh: number;
  iLeak: number;
  password: number;
  nwSelection: number;
  ctSecond: number;
  ctPrimary: number;
  ptSecond: number;
  ptPrimary: number;
  slaveId: number;
  baudrate: number;
  parity: number;
  stopBit: number;
  factoryDefault: number;
  resetKwh: number;
  resetkVAh: number;
  resetkVArh: number;
}

export type TCabinetTimeSlotResult = {
  index: number;
  startTime: string;
  endTime: string;
  power: number;
}

export type TCabinetGetStatusResult = {
  id: number;
  pmConnect: number;
  lcConnect: number;
  plcConnect: number;
  slgConnect: number;
}

export type TCreateCabinetResult = {
  idPayload: string;
  otaPayload?: string;
  registerPayload?: string;
  masterKey: string;
  appKey: string;
  cabinetEntityId: number;
}

export type TRegisterDeviceOutput = {
  mqtt: string;
  btKey: string;
  loraKey?: string;
}

export type TKepwareDevice = {
  id: number;
  deviceId: string;
  port: number;
  orderNumber: number;
}

// New types from GraphQL schema
export type TGateway = {
  id: number;
  cabinetEntityId: number;
  loraProfileId: number;
}

export type TNodeModel = {
  id: number;
  productId: string;
  serialNumber: string;
  slotId?: number;
  groupId?: number;
  status: number;
  gatewayId: number;
}

export type TNodeDimSchedule = {
  id: number;
  startHour: number;
  startMinute?: number;
  dimLevel?: number;
  nodeId: number;
}

export type TNodeData = {
  id: number;
  rawData: string;
  ctValue: number;
  ptValue: number;
  dimLevel: number;
  status: number;
  nodeId: number;
}

export type TNodeParam = {
  id: number;
  rawData: string;
  location?: CPoint;
  slotId: number;
}

export type TGetAllNodesOutput = {
  nodes: TNodeModel;
  nodeParams: TNodeParam;
}

// Input types
export type TPagingFilter = {
  count?: number;
  skip?: number;
  orderBy?: TOrderByFilter[];
}

export type TOrderByFilter = {
  field: string;
  order: TOrder[];
}

export type TOrder = "ASC" | "DESC";

export type TGetWardsInput = {
  provinceId?: string;
  districtId?: string;
}

export type TParseMqttInput = {
  message: string;
  type: string;
}

export type TEncryptInput = {
  buffer: string;
  key: string;
}

export type TGetTimescaleDataInput = {
  id: string;
  from: Date;
  to: Date;
}

export type TRunApplicationInput = {
  dbUsername: string;
  dbPassword: string;
  dbName: string;
  dbHost: string;
  dbStorage: string;
  dbPort: number;
  mqttPort: number;
  maskMqttPort: number;
  mqttHost: string;
  otaUrl: string;
  otaAccessToken: string;
  appUrl: string;
  masterKey?: string;
}

export type TUpdateUserInput = {
  id: string;
  phoneNumber?: string;
  password?: string;
  roleId?: number;
  projectIds?: number[];
  name?: string;
  description?: string;
}

export type TCreateUserRoleInput = {
  id?: number;
  name: "ADMIN" | "REGISTRATOR" | "USER";
}

export type TUserRegisterInput = {
  phoneNumber: string;
  password: string;
  roleId: number;
}

export type TUserVerifyInput = {
  phoneNumber: string;
  otp: string;
}

export type TUserLogin = {
  phoneNumber: string;
  password: string;
}

export type TUserLoginInput = {
  input: TUserLogin;
}

export type TCreateAdminEntityInput = {
  username: string;
  password: string;
  aclRoleName: string;
}

export type TRoleAclInput = {
  aclType: number;
  aclPattern: string;
  aclRoleName: string;
  isAllow: boolean;
}

export type TCreateProjectInput = {
  name: string;
  year: number;
  quater: number;
}

export type TControlActionInput = {
  input: {
    deviceId: number;
    action: boolean;
  }
}

export type TDisableBranchInput = {
  input: {
    deviceId: number;
    branch: number;
    status: number;
  }
}

export type TTimeSlotData = {
  onTime: string;
  offTime: string;
}

export type TTimeSlotControlInput = {
  input: {
    deviceId: number;
    branch1TimeSlot: TTimeSlotData;
    branch2TimeSlot: TTimeSlotData;
  }
}

export type THpsTimeSlotControlInput = {
  deviceId: number;
  hpsTimeSlot: TTimeSlotData;
}

export type TCurrentTimeControlInput = {
  input: {
    deviceId: number;
    date: Date;
  }
}

export type TCreateCabinetInfo = {
  identifier: string;
  PENumber?: string;
  address?: string;
  lat?: number;
  lng?: number;
}

export type TRegisterDeviceInput = {
  id: string;
  cabinetInfoId: number;
  lat?: number;
  lng?: number;
  file?: File;
}

export type TUpdateEntityStatusInput = {
  cabinetEntityId: number;
  status: number;
}

export type TCreateKepwareDeviceInput = {
  deviceId: string;
  port: number;
}

export type TControlLoraDimInput = {
  deviceId: number;
  value: number;
}

// New input types from GraphQL schema
export type TGetNodeParamInput = {
  nodeId: string;
}

export type TCreateGatewayInput = {
  cabinetId: number;
  channel: number;
  bandwidth: number;
  dataRate: number;
  codingRate: number;
  slotId: number;
  groupId: number;
  txPower: number;
  valid: number;
}

export type TSetSlotInput = {
  serialNumber: string;
  slotId: number;
  groupId: number;
  txPower: number;
  valid: number;
  relaySteps: number;
  serialNumber1?: number;
  serialNumber2?: number;
  serialNumber3?: number;
  serialNumber4?: number;
}

export type TForceDimAllInput = {
  cabinetId: number;
  duration?: number;
  dimValue: number;
}

export type TForceDimSingleInput = {
  nodeId: number;
  duration?: number;
  dimValue: number;
}

// New input types from GraphQL schema
export type TMapNodeWithGatewayInput = {
  serialNumber: string;
  cabinetId: number;
}

export type TAssociateWithOPCUAInput = {
  cabinetID: number;
  opcuaID: number;
}

export type TDissociateFromCabinetInput = {
  opcuaID: number;
}

export type TDissociateFromOPCUAInput = {
  cabinetID: number;
}

// Query types
export type TGetProvincesOutput = {
  provinces: TProvince[];
}

export type TGetProvincesOnDeployOutput = {
  provincesOnDeploy: TProvince[];
}

export type TGetProvinceByPointOutput = {
  provincetByPoint: TProvince;
}

export type TGetDistrictsOutput = {
  districts: TDistrict[];
}

export type TGetDistrictsOnDeployOutput = {
  districtsOnDeploy: TDistrict[];
}

export type TGetDistrictByPointOutput = {
  districtByPoint: TDistrict;
}

export type TGetWardsOutput = {
  wards: TWard[];
}

export type TGetWardByPointOutput = {
  wardByPoint: TWard;
}

export type TGetUserByIdOutput = {
  getUserById: TUser;
}

export type TGetAllUserRoleOutput = {
  getAllUserRole: TUserRole[];
}

export type TGetUserByFilterOutput = {
  getUserByFilter: TUser[];
}

export type TGetAllRoleOutput = {
  getAllRole: TRole[];
}

export type TGetAclTypeOutput = {
  getAclType: TAclTypeOutput[];
}

export type TGetProjectsOutput = {
  projects: TProject[];
}

export type TParseMqttMessageOutput = {
  parseMqttMessage: string;
}

export type TGetCabinetEntityByIdOutput = {
  getCabinetEntityById: TCabinetEntity;
}

export type TGetCabinetEntityByEntityIdOutput = {
  getCabinetEntityByEntityId: TCabinetEntity;
}

export type TEncryptBinaryMessageOutput = {
  encryptBinaryMessage: string;
}

export type TEncryptMessageOutput = {
  encryptMessage: string;
}

export type TDecryptMessageOutput = {
  decryptMessage: string;
}

export type TGetCabinetEntitiesByUserOutput = {
  getCabinetEntities: TCabinetEntity[];
}

export type TGetCabinetPmlcTimescaleDataOutput = {
  getCabinetPmlcTimescaleData: TCabinetEntityPmlcData[];
}

export type TGetCabinetTimeSlotsOutput = {
  getCabinetTimeSlots: TCabinetTimeSlotResult[];
}

export type TGetCabinetPlcTimescaleDataOutput = {
  getCabinetPlcTimescaleData: TCabinetEntityPlcData[];
}

export type TFakeDataOutput = {
  fakeData: boolean;
}

export type TCabinetEntityParamData = {
  deviceId: number;
  rawData?: string;
  fwVersion?: string;
  imei?: string;
  rssi?: number;
  netProvider?: number;
  netGen?: number;
  lat?: number;
  lng?: number;
  modemRTC?: Date;
  modemTimeSrc?: number;
  hwVersion?: string;
  modemUpdated?: string;
}

export type TCabinetParamDataResult = {
  deviceId: number
  rawData?: string
  fwVersion?: string
  imei?: string
  rssi?: number
  netProvider?: number
  netGen?: number
  lat?: number
  lng?: number
  modemRTC?: Date
  modemTimeSrc?: number
  hwVersion?: string
  modemUpdated?: string
}

export type TGetCabinetParamDataOutput = {
  getCabinetParamData: TCabinetParamDataResult;
}

// New query output types
export type TGetKepwareDevicesOutput = {
  getKepwareDevices: TKepwareDevice[];
}

export type TGetAllNodesQueryOutput = {
  getAllNodes: TGetAllNodesOutput[];
}

export type TGetNodeParamsOutput = {
  getNodeParams: TNodeParam;
}

export type TGetNodeStatusOutput = {
  getNodeStatus: TNodeData;
}

export type TGetNodeDimScheduleOutput = {
  getNodeDimSchedule: TNodeDimSchedule[];
}

// Mutation types
export type TRunApplicationOutput = {
  runApplication: boolean;
}

export type TUpdateUserOutput = {
  updateUser: TUser;
}

export type TCreateUserRoleOutput = {
  createUserRole: boolean;
}

export type TUserRegisterOutput = {
  userRegister: boolean;
}

export type TUserVerifyOutput = {
  userVerify: boolean;
}

export type TUserLoginOutput = {
  userLogin: TUserLoginResponse;
}

export type TCreateAdminEntityOutput = {
  createAdminEntity: boolean;
}

export type TCreateRoleOutput = {
  createRole: boolean;
}

export type TCreateRoleAclOutput = {
  createRoleAcl: boolean;
}

export type TProjectCreateOutput = {
  projectCreate: TProject;
}

export type TCabinetForceControlOutput = {
  cabinetForceControl: boolean;
}

export type TCabinetDisableBranchOutput = {
  cabinetDisableBranch: boolean;
}

export type TCabinetTimeSlotControlOutput = {
  cabinetTimeSlotControl: boolean;
}

export type TCabinetHpsTimeSlotControlOutput = {
  cabinetHpsTimeSlotControl: boolean;
}

export type TCabinetSetRealTimeOutput = {
  cabinetSetRealTime: boolean;
}

export type TCreateCabinetInfoOutput = {
  createCabinetInfo: TCabinetInfo;
}

export type TRegisterProductionCabinetToThisServerOutput = {
  registerProductionCabinetToThisServer: TRegisterDeviceOutput;
}

export type TCreateFactoryCabinetOutput = {
  createFactoryCabinet: TCreateCabinetResult;
}

export type TUpdateCabinetStatusOutput = {
  updateCabinetStatus: boolean;
}

export type TCreateFactoryNodeByUserOutput = {
  createFactoryNodeByUser: TCreateCabinetResult;
}

export type TCreateKepwareDeviceOutput = {
  createKepwareDevice: TKepwareDevice;
}

export type TTestSetParamsOutput = {
  testSetParams: boolean;
}

export type TTestSetConnectionOutput = {
  testSetConnection: boolean;
}

export type TTestPLCDataOutput = {
  testPLCData: boolean;
}

export type TTestPMLCDataOutput = {
  testPMLCData: boolean;
}

export type TCabinetLoraDimControlOutput = {
  cabinetLoraDimControl: boolean;
}

// New mutation output types
export type TCreateGatewayOutput = {
  createGateway: TGateway;
}

export type TSetNodeSlotOutput = {
  setNodeSlot: boolean;
}

export type TForceDimAllOutput = {
  forceDimAll: boolean;
}

export type TForceDimSingleOutput = {
  forceDimSingle: boolean;
}

export type TCreateNodeOutput = {
  createNode: TNodeModel;
}

export type TMapNodeWithGatewayOutput = {
  mapNodeWithGateway: TNodeModel;
}

export type TAssociateWithOPCUAOutput = {
  associateWithOPCUADevice: boolean;
}

export type TDissociateFromCabinetOutput = {
  dissociateFromCabinet: boolean;
}

export type TDissociateFromOPCUAOutput = {
  dissociateFromOPCUA: boolean;
}

// Subscription types
export type TGetCabinetStatusOutput = {
  getCabinetStatus: TCabinetGetStatusResult;
}

export type TGetCabinetRssiOutput = {
  getCabinetRssi: number;
}

export type TGetCabinetPmlcDataOutput = {
  getCabinetPmlcData: TCabinetEntityPmlcData;
}

export type TGetCabinetPlcDataOutput = {
  getCabinetPlcData: TCabinetEntityPlcData;
}

// New subscription output type
export type TSubNodeStatusUpdatedOutput = {
  subNodeStatusUpdated: TNodeParam;
}

// GraphQL Queries
export const GET_PROVINCES = gql`
  query getProvinces($paging: PagingFilter) {
    provinces(paging: $paging) {
      name
      displayName
    }
  }
`;

export const GET_PROVINCES_ON_DEPLOY = gql`
  query getProvincesOnDeploy {
    provincesOnDeploy {
      name
      displayName
    }
  }
`;

export const GET_PROVINCE_BY_POINT = gql`
  query getProvinceByPoint($lat: Float!, $lng: Float!) {
    provincetByPoint(lat: $lat, lng: $lng) {
      name
      displayName
    }
  }
`;

export const GET_DISTRICTS = gql`
  query getDistricts($provinceId: String!, $paging: PagingFilter) {
    districts(provinceId: $provinceId, paging: $paging) {
      name
      displayName
      provinceId
      province {
        name
        displayName
      }
    }
  }
`;

export const GET_DISTRICTS_ON_DEPLOY = gql`
  query getDistrictsOnDeploy($provinceIds: [String!]!) {
    districtsOnDeploy(provinceIds: $provinceIds) {
      name
      displayName
      provinceId
      province {
        name
        displayName
      }
    }
  }
`;

export const GET_DISTRICT_BY_POINT = gql`
  query getDistrictByPoint($lat: Float!, $lng: Float!) {
    districtByPoint(lat: $lat, lng: $lng) {
      name
      displayName
      provinceId
      province {
        name
        displayName
      }
    }
  }
`;

export const GET_WARDS = gql`
  query getWards($input: GetWardsInput!, $paging: PagingFilter) {
    wards(input: $input, paging: $paging) {
      name
      displayName
      districtId
      district {
        name
        displayName
        provinceId
        province {
          name
          displayName
        }
      }
    }
  }
`;

export const GET_WARD_BY_POINT = gql`
  query getWardByPoint($lat: Float!, $lng: Float!) {
    wardByPoint(lat: $lat, lng: $lng) {
      name
      displayName
      districtId
      district {
        name
        displayName
        provinceId
        province {
          name
          displayName
        }
      }
    }
  }
`;

export const GET_USER_BY_ID = gql`
  query getUserById($id: Float!) {
    getUserById(id: $id) {
      id
      phoneNumber
      isVerify
      isActivated
      location {
        coordinates
      }
    }
  }
`;

export const GET_ALL_USER_ROLE = gql`
  query getAllUserRole {
    getAllUserRole {
      id
    }
  }
`;

export const GET_USER_BY_FILTER = gql`
  query getUserByFilter($paging: PagingFilter) {
    getUserByFilter(paging: $paging) {
      id
      phoneNumber
      isVerify
      isActivated
      location {
        coordinates
      }
    }
  }
`;

export const GET_ALL_ROLE = gql`
  query getAllRole {
    getAllRole {
      role_name
    }
  }
`;

export const GET_ACL_TYPE = gql`
  query getAclType {
    getAclType {
      label
      value
    }
  }
`;

export const GET_PROJECTS = gql`
  query getProjects($paging: PagingFilter) {
    projects(paging: $paging) {
      id
      name
      year
      quater
      createdAt
      updatedAt
    }
  }
`;

export const PARSE_MQTT_MESSAGE = gql`
  query parseMqttMessage($input: TParseMqttInput!) {
    parseMqttMessage(input: $input)
  }
`;

export const GET_CABINET_ENTITY_BY_ID = gql`
  query getCabinetEntityById($id: Float!) {
    getCabinetEntityById(id: $id) {
      id
      hwVersion
      fwVersion
      appKey
    }
  }
`;

export const GET_CABINET_ENTITY_BY_ENTITY_ID = gql`
  query getCabinetEntityByEntityId($entityId: String!) {
    getCabinetEntityByEntityId(entityId: $entityId) {
      id
      hwVersion
      fwVersion
      appKey
    }
  }
`;

export const ENCRYPT_BINARY_MESSAGE = gql`
  query encryptBinaryMessage($input: EncryptInput!) {
    encryptBinaryMessage(input: $input)
  }
`;

export const ENCRYPT_MESSAGE = gql`
  query encryptMessage($input: EncryptInput!) {
    encryptMessage(input: $input)
  }
`;

export const DECRYPT_MESSAGE = gql`
  query decryptMessage($input: EncryptInput!) {
    decryptMessage(input: $input)
  }
`;

export const GET_CABINET_ENTITIES_BY_USER = gql`
  query getCabinetEntities {
    getCabinetEntities {
      id
      hwVersion
      fwVersion
      appKey
    }
  }
`;

export const GET_CABINET_PMLC_TIMESCALE_DATA = gql`
  query getCabinetPmlcTimescaleData($input: GetTimescaleDataInput!) {
    getCabinetPmlcTimescaleData(input: $input) {
      deviceId
      rawData
      v1n
      v2n
      v3n
      avgV
      v12
      v23
      v31
      avgLL
      i1
      i2
      i3
      avgI
      kW1
      kW2
      kW3
      totalkW
      kVA1
      kVA2
      kVA3
      totalkVA
      kVAr1
      kVAr2
      kVAr3
      totalkVAr
      PF1
      PF2
      PF3
      avgPF
      freq
      kWh
      iLeak
      password
      nwSelection
      ctSecond
      ctPrimary
      ptSecond
      ptPrimary
      slaveId
      baudrate
      parity
      stopBit
      factoryDefault
      resetKwh
      resetkVAh
      resetkVArh
    }
  }
`;

export const GET_CABINET_TIME_SLOTS = gql`
  query getCabinetTimeSlots($cabinetDeviceId: Float!) {
    getCabinetTimeSlots(cabinetDeviceId: $cabinetDeviceId) {
      index
      startTime
      endTime
      power
    }
  }
`;

export const GET_CABINET_PLC_TIMESCALE_DATA = gql`
  query getCabinetPlcTimescaleData($input: GetTimescaleDataInput!) {
    getCabinetPlcTimescaleData(input: $input) {
      deviceId
      rawData
      rtcTime
      out1Status
      out2Status
      br1Status
      br1OnTime
      br1OffTime
      br2Status
      br2OnTime
      br2OffTime
      forceState
      forceTotalTime
      forceCurrentTime
      forceRemainTime
      manualState
      manualTotalTime
      manualCurrentTime
      manualRemainTime
      doorState
      disBr1
      disBr2
      hpsOnTime
      hpsOffTime
    }
  }
`;

export const FAKE_DATA = gql`
  query fakeData {
    fakeData
  }
`;

export const GET_CABINET_PARAM = gql`
  query getCabinetParamData($cabinetDeviceId: Float!) {
    getCabinetParamData(cabinetDeviceId: $cabinetDeviceId) {
      deviceId
      rawData
      fwVersion
      imei
      rssi
      netProvider
      netGen
      lat
      lng
      modemRTC
      modemTimeSrc
      hwVersion
      modemUpdated
    }
  }
`;

// New GraphQL Queries
export const GET_KEPWARE_DEVICES = gql`
  query getKepwareDevices {
    getKepwareDevices {
      id
      deviceId
      port
      orderNumber
    }
  }
`;

export const GET_ALL_NODES = gql`
  query getAllNodes($cabinetId: Float!) {
    getAllNodes(cabinetId: $cabinetId) {
      nodes {
        id
        productId
        serialNumber
        slotId
        groupId
        status
        gatewayId
      }
      nodeParams {
        id
        rawData
        location {
          coordinates
        }
        slotId
      }
    }
  }
`;

export const GET_NODE_PARAMS = gql`
  query getNodeParams($input: GetNodeParamInput!) {
    getNodeParams(input: $input) {
      id
      rawData
      location {
        coordinates
      }
      slotId
    }
  }
`;

export const GET_NODE_STATUS = gql`
  query getNodeStatus($nodeId: String!) {
    getNodeStatus(nodeId: $nodeId) {
      id
      rawData
      ctValue
      ptValue
      dimLevel
      status
      nodeId
    }
  }
`;

export const GET_NODE_DIM_SCHEDULE = gql`
  query getNodeDimSchedule($nodeId: String!) {
    getNodeDimSchedule(nodeId: $nodeId) {
      id
      startHour
      startMinute
      dimLevel
      nodeId
    }
  }
`;

// GraphQL Mutations
export const RUN_APPLICATION = gql`
  mutation runApplication($input: RunApplicationInput!) {
    runApplication(input: $input)
  }
`;

export const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      phoneNumber
      isVerify
      isActivated
      location {
        coordinates
      }
    }
  }
`;

export const CREATE_USER_ROLE = gql`
  mutation createUserRole($input: CreateUserRoleInput!) {
    createUserRole(input: $input)
  }
`;

export const USER_REGISTER = gql`
  mutation userRegister($input: UserRegisterInput!) {
    userRegister(input: $input)
  }
`;

export const USER_VERIFY = gql`
  mutation userVerify($input: UserVerifyInput!) {
    userVerify(input: $input)
  }
`;

export const USER_LOGIN = gql`
  mutation userLogin($input: UserLoginInput!) {
    userLogin(input: $input) {
      accessToken
      user {
        id
        phoneNumber
        isVerify
        isActivated
        location {
          coordinates
        }
      }
    }
  }
`;

export const CREATE_ADMIN_ENTITY = gql`
  mutation createAdminEntity($input: CreateAdminEntityInput!) {
    createAdminEntity(input: $input)
  }
`;

export const CREATE_ROLE = gql`
  mutation createRole($name: String!) {
    createRole(name: $name)
  }
`;

export const CREATE_ROLE_ACL = gql`
  mutation createRoleAcl($input: RoleAclInput!) {
    createRoleAcl(input: $input)
  }
`;

export const PROJECT_CREATE = gql`
  mutation projectCreate($createProjectInput: CreateProjectInput!) {
    projectCreate(createProjectInput: $createProjectInput) {
      id
      name
      year
      quater
      createdAt
      updatedAt
    }
  }
`;

export const CABINET_FORCE_CONTROL = gql`
  mutation cabinetForceControl($input: ControlActionInput!) {
    cabinetForceControl(input: $input)
  }
`;

export const CABINET_DISABLE_BRANCH = gql`
  mutation cabinetDisableBranch($input: DisableBranchInput!) {
    cabinetDisableBranch(input: $input)
  }
`;

export const CABINET_TIME_SLOT_CONTROL = gql`
  mutation cabinetTimeSlotControl($input: TimeSlotControlInput!) {
    cabinetTimeSlotControl(input: $input)
  }
`;

export const CABINET_HPS_TIME_SLOT_CONTROL = gql`
  mutation cabinetHpsTimeSlotControl($input: HpsTimeSlotControlInput!) {
    cabinetHpsTimeSlotControl(input: $input)
  }
`;

export const CABINET_SET_REAL_TIME = gql`
  mutation cabinetSetRealTime($input: CurrentTimeControlInput!) {
    cabinetSetRealTime(input: $input)
  }
`;

export const CREATE_CABINET_INFO = gql`
  mutation createCabinetInfo($input: CreateCabinetInfo!) {
    createCabinetInfo(input: $input) {
      id
      cabinetId
      identifier
      PENumber
      address
      location {
        coordinates
      }
      isRegistered
      registeredAt
      wardId
      ward {
        name
        displayName
        districtId
        district {
          name
          displayName
          provinceId
          province {
            name
            displayName
          }
        }
      }
    }
  }
`;

export const REGISTER_PRODUCTION_CABINET_TO_THIS_SERVER = gql`
  mutation registerProductionCabinetToThisServer($cabinetInput: RegisterDeviceInput!) {
    registerProductionCabinetToThisServer(cabinetInput: $cabinetInput) {
      mqtt
      btKey
      loraKey
    }
  }
`;

export const CREATE_FACTORY_CABINET = gql`
  mutation createFactoryCabinet($input: String!) {
    createFactoryCabinet(input: $input) {
      idPayload
      otaPayload
      registerPayload
      masterKey
      appKey
      cabinetEntityId
    }
  }
`;

export const UPDATE_CABINET_STATUS = gql`
  mutation updateCabinetStatus($input: UpdateEntityStatusInput!) {
    updateCabinetStatus(input: $input)
  }
`;

export const CREATE_FACTORY_NODE_BY_USER = gql`
  mutation createFactoryNodeByUser($input: String!) {
    createFactoryNodeByUser(input: $input) {
      idPayload
      otaPayload
      registerPayload
      masterKey
      appKey
      cabinetEntityId
    }
  }
`;

export const CREATE_KEPWARE_DEVICE = gql`
  mutation createKepwareDevice($input: CreateKepwareDeviceInput!) {
    createKepwareDevice(input: $input) {
      id
      deviceId
      port
      orderNumber
    }
  }
`;

export const TEST_SET_PARAMS = gql`
  mutation testSetParams($id: Float!) {
    testSetParams(id: $id)
  }
`;

export const TEST_SET_CONNECTION = gql`
  mutation testSetConnection($id: Float!) {
    testSetConnection(id: $id)
  }
`;

export const TEST_PLC_DATA = gql`
  mutation testPLCData($id: Float!) {
    testPLCData(id: $id)
  }
`;

export const TEST_PMLC_DATA = gql`
  mutation testPMLCData($id: Float!) {
    testPMLCData(id: $id)
  }
`;

export const CABINET_LORA_DIM_CONTROL = gql`
  mutation cabinetLoraDimControl($input: ControlLoraDimInput!) {
    cabinetLoraDimControl(input: $input)
  }
`;

// New GraphQL Mutations
export const CREATE_GATEWAY = gql`
  mutation createGateway($input: CreateGatewayInput!) {
    createGateway(input: $input) {
      id
      cabinetEntityId
      loraProfileId
    }
  }
`;

export const SET_NODE_SLOT = gql`
  mutation setNodeSlot($input: SetSlotInput!) {
    setNodeSlot(input: $input)
  }
`;

export const FORCE_DIM_ALL = gql`
  mutation forceDimAll($input: ForceDimAllInput!) {
    forceDimAll(input: $input)
  }
`;

export const FORCE_DIM_SINGLE = gql`
  mutation forceDimSingle($input: ForceDimSingleInput!) {
    forceDimSingle(input: $input)
  }
`;

export const CREATE_NODE = gql`
  mutation createNode($input: String!) {
    createNode(input: $input) {
      id
      productId
      serialNumber
      slotId
      groupId
      status
      gatewayId
    }
  }
`;

export const MAP_NODE_WITH_GATEWAY = gql`
  mutation mapNodeWithGateway($input: MapNodeWithGatewayInput!) {
    mapNodeWithGateway(input: $input) {
      id
      productId
      serialNumber
      slotId
      groupId
      status
      gatewayId
    }
  }
`;

export const ASSOCIATE_WITH_OPCUA_DEVICE = gql`
  mutation associateWithOPCUADevice($input: AssociateWithOPCUAInput!) {
    associateWithOPCUADevice(input: $input)
  }
`;

export const DISSOCIATE_FROM_CABINET = gql`
  mutation dissociateFromCabinet($input: DissociateFromCabinetInput!) {
    dissociateFromCabinet(input: $input)
  }
`;

export const DISSOCIATE_FROM_OPCUA = gql`
  mutation dissociateFromOPCUA($input: DissociateFromOPCUAInput!) {
    dissociateFromOPCUA(input: $input)
  }
`;

// GraphQL Subscriptions
export const GET_CABINET_STATUS = gql`
  subscription getCabinetStatus($cabinetDeviceId: Float!) {
    getCabinetStatus(cabinetDeviceId: $cabinetDeviceId) {
      id
      pmConnect
      lcConnect
      plcConnect
      slgConnect
    }
  }
`;

export const GET_CABINET_RSSI = gql`
  subscription getCabinetRssi($cabinetDeviceId: Float!) {
    getCabinetRssi(cabinetDeviceId: $cabinetDeviceId)
  }
`;

export const GET_CABINET_PMLC_DATA = gql`
  subscription getCabinetPmlcData($cabinetDeviceId: Float!) {
    getCabinetPmlcData(cabinetDeviceId: $cabinetDeviceId) {
      deviceId
      rawData
      v1n
      v2n
      v3n
      avgV
      v12
      v23
      v31
      avgLL
      i1
      i2
      i3
      avgI
      kW1
      kW2
      kW3
      totalkW
      kVA1
      kVA2
      kVA3
      totalkVA
      kVAr1
      kVAr2
      kVAr3
      totalkVAr
      PF1
      PF2
      PF3
      avgPF
      freq
      kWh
      iLeak
      password
      nwSelection
      ctSecond
      ctPrimary
      ptSecond
      ptPrimary
      slaveId
      baudrate
      parity
      stopBit
      factoryDefault
      resetKwh
      resetkVAh
      resetkVArh
    }
  }
`;

export const GET_CABINET_PLC_DATA = gql`
  subscription getCabinetPlcData($cabinetDeviceId: Float!) {
    getCabinetPlcData(cabinetDeviceId: $cabinetDeviceId) {
      deviceId
      rawData
      rtcTime
      out1Status
      out2Status
      br1Status
      br1OnTime
      br1OffTime
      br2Status
      br2OnTime
      br2OffTime
      forceState
      forceTotalTime
      forceCurrentTime
      forceRemainTime
      manualState
      manualTotalTime
      manualCurrentTime
      manualRemainTime
      doorState
      disBr1
      disBr2
      hpsOnTime
      hpsOffTime
    }
  }
`;

// New GraphQL Subscription
export const SUB_NODE_STATUS_UPDATED = gql`
  subscription subNodeStatusUpdated($nodeId: String!) {
    subNodeStatusUpdated(nodeId: $nodeId) {
      id
      rawData
      location {
        coordinates
      }
      slotId
    }
  }
`;

// Legacy types for backward compatibility
export type TAuthResponse = {
  accessToken: string;
  user: TUser;
};

export type TLoginResult = {
  userLogin: TAuthResponse;
};

export type TRefreshTokenResult = {
  refreshToken: TAuthResponse;
};

export type TGetCabinetDataInput = {
  cabinetDeviceId: number;
}